import { Injectable } from '@angular/core';

import { InventoryDevice } from '@shure/cloud/shared/models/devices';
import { DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { OneToManyAssociation } from '@shure/shared/typescript/data-structures';

export interface HostDevice {
	id: string;
	model: DeviceModel;
	firmwareVersion?: string;
}

export interface GatewayDevice {
	id: string;
}

@Injectable({ providedIn: 'root' })
export class DeviceRelationshipService {
	private readonly hostToProxiesMap = new OneToManyAssociation<HostDevice, string>(
		(hostDevice) => hostDevice.id,
		(proxiedDeviceId) => proxiedDeviceId
	);
	private readonly gatewayToManagedMap = new OneToManyAssociation<GatewayDevice, string>(
		(gatewayDevice) => gatewayDevice.id,
		(managedDeviceId) => managedDeviceId
	);

	public registerProxiedDevices(hostDevice: InventoryDevice): void {
		const proxiedDeviceIds = (hostDevice.features.proxiedDevices?.proxiedDevices ?? []).map((device) => device.id);

		// If the host device is being proxied by another device, remove that relationship first. This could
		// happen when a device such as an APXD2 becomes managed by an IoT gateway.
		const proxyDeviceForHost = this.getHostDevice(hostDevice.id);
		if (proxiedDeviceIds.length > 0 && proxyDeviceForHost) {
			this.hostToProxiesMap.removeChild(hostDevice.id);
		}

		this.hostToProxiesMap.set(
			{
				id: hostDevice.id,
				model: hostDevice.model,
				firmwareVersion: hostDevice.features.firmware?.version
			},
			proxiedDeviceIds
		);
	}

	public registerManagedDevices(gatewayDevice: InventoryDevice): void {
		const managedDeviceIds = [];
		for (const managedDevice of gatewayDevice.features.cloudGateway?.managedDevices ?? []) {
			// Only register managed devices that are full device nodes. Ignore partial devices since they won't
			// be present in the inventory.
			if (managedDevice.isDeviceNode) {
				managedDeviceIds.push(managedDevice.nodeId);
			}
		}

		this.gatewayToManagedMap.set(gatewayDevice, managedDeviceIds);
	}

	public removeDevice(deviceId: string): void {
		this.hostToProxiesMap.removeAssociations(deviceId);
		this.gatewayToManagedMap.removeAssociations(deviceId);
	}

	public getHostDevice(proxiedDeviceId: string): HostDevice | undefined {
		return this.hostToProxiesMap.getParent(proxiedDeviceId);
	}

	public getProxiedDevices(hostDeviceId: string): string[] {
		return this.hostToProxiesMap.getChildren(hostDeviceId) ?? [];
	}

	public getGatewayDevice(managedDeviceId: string): GatewayDevice | undefined {
		return this.gatewayToManagedMap.getParent(managedDeviceId);
	}
}
